const ProductCardSwatchesBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class ProductCardSwatches extends ProductCardSwatchesBase {
  static preloadCache = new Map();

  connectedCallback() {
    this.card = this.closest?.('.product-card-wrapper');
    this.boundHandleCardPointerMove ||= this.handleCardPointerMove.bind(this);
    this.boundHandleCardPointerLeave ||= this.handleCardPointerLeave.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerover', this.handleIntent);
    this.addEventListener('focusin', this.handleIntent);
    this.addEventListener('pointerdown', this.handleIntent);
    this.card?.addEventListener('pointermove', this.boundHandleCardPointerMove);
    this.card?.addEventListener('pointerleave', this.boundHandleCardPointerLeave);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerover', this.handleIntent);
    this.removeEventListener('focusin', this.handleIntent);
    this.removeEventListener('pointerdown', this.handleIntent);
    this.card?.removeEventListener('pointermove', this.boundHandleCardPointerMove);
    this.card?.removeEventListener('pointerleave', this.boundHandleCardPointerLeave);
    this.card?.classList.remove('card-wrapper--media-hovered');
    this.selectionRequestId = (this.selectionRequestId || 0) + 1;
    this.deferredSecondary = null;
  }

  handleIntent(event) {
    const button = event.target.closest('[data-card-swatch]');
    if (!button || !this.contains(button)) return;

    this.preloadSwatch(button);
  }

  async handleClick(event) {
    const button = event.target.closest('[data-card-swatch]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();
    this.deferredSecondary = null;
    const requestId = (this.selectionRequestId || 0) + 1;
    this.selectionRequestId = requestId;
    const preloads = this.preloadSwatch(button);

    const primaryReady = await preloads.primary;
    if (requestId !== this.selectionRequestId || !primaryReady) return;

    const deferSecondary = Boolean(preloads.secondary || preloads.secondaryPayload);
    if (!preloads.secondary && preloads.secondaryPayload) {
      this.deferredSecondary = { button, requestId, payload: preloads.secondaryPayload, promise: null };
    }
    this.selectSwatch(button, { deferSecondary });
    if (!preloads.secondary) return;

    const secondaryReady = await preloads.secondary;
    if (requestId !== this.selectionRequestId || !secondaryReady) return;
    this.applySelectedSecondary(button);
  }

  handleCardPointerMove(event) {
    const card = this.card || this.closest?.('.product-card-wrapper');
    if (!card?.classList.contains('card-wrapper--swatch-selected')) {
      card?.classList.remove('card-wrapper--media-hovered');
      return;
    }

    const media = card.querySelector('.card__media');
    if (!media) return;

    const rect = media.getBoundingClientRect();
    const isMediaHovered =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    card.classList.toggle('card-wrapper--media-hovered', isMediaHovered);
    if (
      isMediaHovered &&
      this.deferredSecondary &&
      this.deferredSecondary.requestId === this.selectionRequestId
    ) {
      this.loadDeferredSecondary(card);
    }
  }

  handleCardPointerLeave() {
    this.card?.classList.remove('card-wrapper--media-hovered');
  }

  loadDeferredSecondary(card) {
    const deferred = this.deferredSecondary;
    if (!deferred || deferred.promise) return;

    const cardImage = card.querySelector('[data-card-secondary-image]') || card.querySelector('[data-card-primary-image]');
    deferred.promise = ProductCardSwatches.preloadImage(deferred.payload, {
      priority: 'auto',
      sizes: ProductCardSwatches.responsiveImageSizes(card, cardImage),
    });
    deferred.promise.then((ready) => {
      if (this.deferredSecondary !== deferred || deferred.requestId !== this.selectionRequestId) return;
      if (!ready) {
        deferred.promise = null;
        return;
      }

      this.applySelectedSecondary(deferred.button, card, deferred.payload);
      this.deferredSecondary = null;
    });
  }

  preloadSwatch(button) {
    if (!button) return;

    const card = this.closest('.product-card-wrapper');
    const cardImage = card?.querySelector('[data-card-primary-image]');
    const sizes = ProductCardSwatches.responsiveImageSizes(card, cardImage);
    const primary = ProductCardSwatches.preloadImage(ProductCardSwatches.imagePayload(button, 'primary'), {
      priority: 'high',
      sizes,
    });

    const secondaryPayload = ProductCardSwatches.imagePayload(button, 'secondary');
    let secondary = null;
    if (!ProductCardSwatches.dataSaverEnabled() && secondaryPayload) {
      secondary = ProductCardSwatches.preloadImage(secondaryPayload, {
        priority: 'auto',
        sizes,
      });
    }

    return { primary, secondary, secondaryPayload };
  }

  selectSwatch(button, { deferSecondary = false } = {}) {
    if (!button) return;

    const card = this.closest('.product-card-wrapper');
    if (!card) return;

    this.querySelectorAll('[data-card-swatch]').forEach((swatch) => {
      swatch.setAttribute('aria-pressed', String(swatch === button));
    });

    const primary = ProductCardSwatches.imagePayload(button, 'primary');
    const secondary = ProductCardSwatches.imagePayload(button, 'secondary');
    const primaryImage = card.querySelector('[data-card-primary-image]');
    const sizes = ProductCardSwatches.responsiveImageSizes(card, primaryImage);
    if (primary) {
      ProductCardSwatches.updateImage(primaryImage, { ...primary, sizes }, {
        preserveLoading: true,
      });
    }
    card.classList.toggle('card-wrapper--swatch-selected', true);
    card.classList.toggle('card-wrapper--media-hovered', false);
    if (deferSecondary) {
      ProductCardSwatches.updateImage(card.querySelector('[data-card-secondary-image]'), null);
      card.classList.toggle('card-wrapper--has-secondary', false);
    } else {
      this.applySelectedSecondary(button, card, secondary);
    }

    if (button.dataset.variantUrl) {
      card.querySelectorAll('[data-card-product-link]').forEach((link) => {
        link.href = button.dataset.variantUrl;
      });
    }

    const priceTemplate = this.querySelector(`template[data-card-variant-price="${button.dataset.variantId}"]`);
    const currentPrice = card.querySelector('.card-information > .price');
    const nextPrice = priceTemplate?.content?.firstElementChild?.cloneNode(true);
    if (currentPrice && nextPrice) {
      nextPrice.setAttribute('aria-live', 'polite');
      currentPrice.replaceWith(nextPrice);
    }
  }

  applySelectedSecondary(button, card = this.closest('.product-card-wrapper'), secondary = null) {
    if (!card) return;

    const payload = secondary || ProductCardSwatches.imagePayload(button, 'secondary');
    const image = card.querySelector('[data-card-secondary-image]');
    const fallbackImage = image || card.querySelector('[data-card-primary-image]');
    const sizes = ProductCardSwatches.responsiveImageSizes(card, fallbackImage);
    ProductCardSwatches.updateImage(image, payload ? { ...payload, sizes } : null);
    card.classList.toggle('card-wrapper--has-secondary', Boolean(payload));
  }

  static responsiveImageSizes(card, fallbackImage) {
    const renderedWidth = card?.querySelector?.('.card__media')?.getBoundingClientRect?.().width;
    if (Number.isFinite(renderedWidth) && renderedWidth > 0) {
      return `${Math.ceil(renderedWidth * 1.03)}px`;
    }

    return (
      fallbackImage?.getAttribute?.('sizes') ||
      card?.querySelector?.('[data-card-primary-image]')?.getAttribute?.('sizes') ||
      ''
    );
  }

  static imagePayload(button, prefix) {
    const key = (suffix) => `${prefix}${suffix}`;
    const src = button?.dataset?.[key('Src')];
    if (!src) return null;

    return {
      src,
      srcset: button.dataset[key('Srcset')] || '',
      alt: button.dataset[key('Alt')] || '',
      width: button.dataset[key('Width')] || '',
      height: button.dataset[key('Height')] || '',
    };
  }

  static dataSaverEnabled() {
    return typeof navigator !== 'undefined' && navigator.connection?.saveData === true;
  }

  static preloadImage(payload, { priority = 'auto', sizes = '' } = {}) {
    if (!payload) return Promise.resolve(true);
    if (typeof Image === 'undefined') return Promise.resolve(false);

    const cacheKey = `${sizes}|${payload.srcset}|${payload.src}`;
    if (ProductCardSwatches.preloadCache.has(cacheKey)) {
      return ProductCardSwatches.preloadCache.get(cacheKey);
    }

    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = priority;
    if (sizes) image.sizes = sizes;
    if (payload.srcset) image.srcset = payload.srcset;
    image.src = payload.src;

    const decoded =
      typeof image.decode === 'function'
        ? image.decode().then(() => true, () => false)
        : new Promise((resolve) => {
            image.onload = () => resolve(true);
            image.onerror = () => resolve(false);
            if (image.complete) resolve(image.naturalWidth > 0);
          });
    ProductCardSwatches.preloadCache.set(cacheKey, decoded);
    decoded.then((ready) => {
      if (!ready && ProductCardSwatches.preloadCache.get(cacheKey) === decoded) {
        ProductCardSwatches.preloadCache.delete(cacheKey);
      }
    });
    return decoded;
  }

  static updateImage(image, payload, { preserveLoading = false } = {}) {
    if (!image) return;

    if (!payload) {
      image.hidden = true;
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      return;
    }

    image.hidden = false;
    const { src, srcset, ...attributes } = payload;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value) image.setAttribute(name, value);
      else image.removeAttribute(name);
    });
    if (srcset) image.setAttribute('srcset', srcset);
    else image.removeAttribute('srcset');
    if (src) image.setAttribute('src', src);
    else image.removeAttribute('src');
    if (!preserveLoading) image.setAttribute('loading', 'lazy');
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('product-card-swatches')) {
  customElements.define('product-card-swatches', ProductCardSwatches);
}

if (typeof module !== 'undefined') module.exports = ProductCardSwatches;
